import * as React from "react";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Bell, Home, LogOut, Settings, ShieldCheck, Users } from "lucide-react";
import { AppShell, Boton, type EntradaNav } from "@iavmx/ui";
import { leer } from "../api";
import { useSesion } from "../sesion";
import type { Inicio } from "../tipos";
import { aplicarTema, temaDePreferencias } from "../tema";

const PUBLICAS = ["/entrar", "/entrar/invitacion", "/entrar/callback", "/auth/confirmar", "/ui"];

type Ruta = "/" | "/directorio" | "/avisos" | "/ajustes" | "/admin";

export function Raiz() {
  const ruta = useRouterState({ select: (s) => s.location.pathname });
  const navegar = useNavigate();
  const { cargando, correo, yo, estadoYo, cerrar, api } = useSesion();
  const publica = PUBLICAS.includes(ruta);

  // Quién no tiene sesión va a /entrar; quien la tiene pero no está en el directorio, al callback que lo explica;
  // quien todavía no completó su perfil, a la primera entrada.
  React.useEffect(() => {
    if (cargando || publica) return;
    if (!correo) void navegar({ to: "/entrar", replace: true });
    else if (estadoYo === "sin_directorio") void navegar({ to: "/auth/confirmar", replace: true });
    else if (estadoYo === "ok" && yo && !yo.perfilCompleto && ruta !== "/bienvenida")
      void navegar({ to: "/bienvenida", replace: true });
  }, [cargando, publica, correo, estadoYo, yo, ruta, navegar]);

  // La preferencia de tema guardada manda al abrir el portal (aplica en todas las herramientas).
  const temaGuardado = temaDePreferencias(yo?.preferencias);
  React.useEffect(() => {
    if (temaGuardado) aplicarTema(temaGuardado);
  }, [temaGuardado]);

  const inicio = useQuery({
    queryKey: ["inicio"],
    queryFn: () => api.api.inicio.$get().then((r) => leer<Inicio>(r)),
    enabled: estadoYo === "ok",
    refetchInterval: 60_000,
  });
  const sinLeer = inicio.data?.avisosSinLeer ?? 0;

  if (publica || ruta === "/bienvenida") return <Outlet />;
  if (cargando || (correo && estadoYo === "cargando"))
    return (
      <div className="flex min-h-screen items-center justify-center bg-fondo font-ui text-sm text-tinta-tenue" aria-busy="true">
        Abriendo el portal…
      </div>
    );
  if (!correo || !yo) return null;

  const entradas: { etiqueta: string; to: Ruta; icono: React.ReactNode; solo?: boolean }[] = [
    { etiqueta: "Inicio", to: "/", icono: <Home /> },
    { etiqueta: "Directorio", to: "/directorio", icono: <Users /> },
    { etiqueta: sinLeer ? `Avisos · ${sinLeer}` : "Avisos", to: "/avisos", icono: <Bell /> },
    { etiqueta: "Ajustes", to: "/ajustes", icono: <Settings /> },
    { etiqueta: "Administración", to: "/admin", icono: <ShieldCheck />, solo: yo.esAdmin },
  ];
  const nav: EntradaNav[] = entradas
    .filter((e) => e.solo !== false)
    .map((e) => ({
      etiqueta: e.etiqueta,
      href: e.to,
      icono: e.icono,
      activo: ruta === e.to,
      render: ({ className, children }) => (
        <Link to={e.to} className={className} aria-current={ruta === e.to ? "page" : undefined}>
          {children}
        </Link>
      ),
    }));
  const otras = yo.herramientas
    .filter((h) => h.tiene)
    .map((h) => ({ etiqueta: h.nombre, href: h.url }));
  return (
    <>
      <a
        href="#contenido"
        className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:bg-fondo focus:p-2 focus:ring-2 focus:ring-ring"
      >
        Ir al contenido
      </a>
      <AppShell
        producto="Portal"
        nav={nav}
        persona={{ nombre: yo.nombre, correo: yo.correo, avatar: yo.fotoUrl }}
        otrasApps={otras}
        acciones={
          <>
            <Boton variante="fantasma" tamano="icono" asChild aria-label={sinLeer ? `Avisos, ${sinLeer} sin leer` : "Avisos"}>
              <Link to="/avisos" className="relative">
                <Bell />
                {sinLeer > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 min-w-4 rounded-full bg-tinta px-1 text-center font-ui text-[10px] font-bold leading-4 text-tinta-inversa" aria-hidden="true">
                    {sinLeer}
                  </span>
                )}
              </Link>
            </Boton>
            <Boton
              variante="fantasma"
              tamano="sm"
              className="min-h-10"
              aria-label="Salir"
              onClick={() => void cerrar().then(() => navegar({ to: "/entrar" }))}
            >
              <LogOut /> <span className="hidden sm:inline">Salir</span>
            </Boton>
          </>
        }
      >
        <div id="contenido">
          <Outlet />
        </div>
      </AppShell>
    </>
  );
}
